/*
 * Content Hub Sequences API layer (PlunkDocs specs/SEQUENCES.md §7.1, §7.3, §7.4; mockup sequences-2026-10-05.html).
 *
 * Every route is on /v1/content/sequences/* (AnalyticsService SequencesController, built in parallel with this UI).
 * Fail-soft rule (images-api.js precedent): a 404 / 405 / 501 means "not deployed yet" and resolves to null, so the
 * views render the "not available yet" line instead of an error. Ids stay strings end to end (json-bigint).
 *
 * House rules baked in here: no credit amounts anywhere (the only credit message is the insufficient-credits state
 * with the purchase path on a 402), polling only (10 s per in-flight render), the localStorage batch key so a render
 * that outlives the page re-attaches on the next open.
 */
import endpoints from "../../endpoints";
import { call, softFail, isSoft, ApiError, toast } from "../audiences/api";
export { softFail, isSoft, ApiError, toast };
const ROOT = String(endpoints.product && endpoints.product.root || "");
const SEQ = `${ROOT}/content/sequences`;
const CONTENT = `${ROOT}/content`;
export const POLL_MS = 10000;
export const MAX_PICKS = 40;
export const BUY_URL = "https://account.plunk.io/?from=app";
export const FORMATS = [["16:9", "16:9", "YouTube, site", 1920, 1080], ["9:16", "9:16", "Reels, Shorts, TikTok", 1080, 1920], ["1:1", "1:1", "Feed", 1080, 1080]];
export const PURPOSES = [["promo", "Promo"], ["tutorial", "Tutorial"], ["walkthrough", "Walkthrough"], ["custom", "Custom"]];
export const PURPOSE_HINT = {
  promo: "A hook, three to five beats, a call to action.",
  tutorial: "Keeps your order, names chapters, keeps recordings at their real length.",
  walkthrough: "Narration is written from the product row: what, key attributes, where it is used, why now, get.",
  custom: "The brief and the pieces, one card per piece, no structural rule."
};
export const TRANSITIONS = [["cut", "Cut"], ["crossfade", "Fade"], ["slide", "Slide"]];
export const MOTIONS = [["auto", "Auto"], ["push_in", "Push in"], ["pull_out", "Pull out"], ["pan_right", "Pan right"], ["pan_down", "Pan down"], ["none", "None"]];
export const FITS = [["auto", "Auto"], ["cover", "Cover"], ["contain", "Contain"]];
export const AUDIO_MODES = [["duck", "Duck"], ["mute", "Mute"], ["keep", "Keep"]];
export const TARGETS = [[45, "About 45 seconds"], [30, "About 30 seconds"], [60, "About 60 seconds"], [0, "Let the pieces decide"]];
/** Safe areas per format (fractions of the frame): lower third sits inside the top inset, captions inside the bottom one. */
export const SAFE = {
  "16:9": {
    l: 0.04,
    r: 0.04,
    t: 0.10,
    b: 0.075
  },
  "9:16": {
    l: 0.05,
    r: 0.05,
    t: 0.14,
    b: 0.20
  },
  "1:1": {
    l: 0.05,
    r: 0.05,
    t: 0.08,
    b: 0.10
  }
};
export const DEFAULT_SETTINGS = {
  captions: true,
  captionStyle: "brand",
  transitionSeconds: 0.6,
  padSeconds: 0.6,
  minSceneSeconds: 3,
  titleCard: null,
  endCard: null,
  targetSeconds: 45,
  musicDuck: true
};
export const idStr = v => v === undefined || v === null ? "" : String(v);
const num = (v, d = null) => v === undefined || v === null || v === "" || isNaN(Number(v)) ? d : Number(v);
const items = d => Array.isArray(d) ? d : d && (d.items || d.Items) || [];
const enc = v => encodeURIComponent(idStr(v));

// ---- normalisers (camelCase in, ids as strings, numbers as numbers) --------

export function normRender(r) {
  if (!r) return null;
  return {
    id: idStr(r.id),
    batchId: idStr(r.batchId),
    format: r.format || "16:9",
    status: function (v) {
      v = String(v || "queued").toLowerCase();
      return v === "ready" || v === "complete" || v === "completed" ? "done" : v;
    }(r.status),
    progress: Math.max(0, Math.min(100, num(r.progress, 0))),
    stage: r.stage || "",
    scriptVersion: num(r.scriptVersion, 0),
    url: r.url || null,
    posterUrl: r.posterUrl || null,
    durationSeconds: num(r.durationSeconds),
    width: num(r.width),
    height: num(r.height),
    assetId: r.assetId ? idStr(r.assetId) : null,
    error: r.error || null,
    createdDate: r.createdDate || null,
    startedDate: r.startedDate || null,
    finishedDate: r.finishedDate || null
  };
}
export const inFlight = r => !!r && (r.status === "queued" || r.status === "rendering");
export function normSegment(s, i) {
  if (!s) return null;
  const card = s.card && typeof s.card === "string" ? (() => {
    try {
      return JSON.parse(s.card);
    } catch (e) {
      return null;
    }
  })() : s.card || null;
  const warnings = Array.isArray(s.warnings) ? s.warnings : s.warnings ? [String(s.warnings)] : [];
  return {
    id: s.id ? idStr(s.id) : null,
    _key: s._key || (s.id ? `s${idStr(s.id)}` : `n${Date.now()}${i || 0}${Math.random().toString(36).slice(2, 6)}`),
    position: num(s.position, i || 0),
    kind: String(s.kind || (card ? "card" : "still")).toLowerCase(),
    assetId: s.assetId ? idStr(s.assetId) : null,
    mediaUrl: s.mediaUrl || s.url || null,
    thumbnailUrl: s.thumbnailUrl || s.posterUrl || null,
    title: s.title || s.assetTitle || "",
    narration: s.narration || "",
    overlayText: s.overlayText || "",
    chapterTitle: s.chapterTitle || "",
    trimStart: num(s.trimStart, 0),
    trimEnd: num(s.trimEnd),
    durationSeconds: num(s.durationSeconds),
    fit: s.fit || "auto",
    motion: s.motion || "auto",
    transitionIn: normTransition(s.transitionIn),
    audioMode: s.audioMode || "duck",
    card,
    narrationSeconds: num(s.narrationSeconds),
    estimatedSeconds: num(s.estimatedSeconds),
    warnings,
    mediaSeconds: num(s.mediaSeconds || s.assetSeconds || s.sourceSeconds),
    width: num(s.width),
    height: num(s.height)
  };
}
export const normTransition = t => {
  const v = String(t || "crossfade").toLowerCase();
  return v === "fade" || v === "dissolve" ? "crossfade" : TRANSITIONS.some(([k]) => k === v) ? v : "crossfade";
};
export function normSequence(d) {
  const s = d && (d.sequence || d.Sequence) || d;
  if (!s || !s.id) return null;
  const formats = Array.isArray(s.formats) ? s.formats : typeof s.formats === "string" ? (() => {
    try {
      return JSON.parse(s.formats);
    } catch (e) {
      return s.formats.split(",").map(x => x.trim()).filter(Boolean);
    }
  })() : ["16:9"];
  const settings = typeof s.settings === "string" ? (() => {
    try {
      return JSON.parse(s.settings);
    } catch (e) {
      return {};
    }
  })() : s.settings || {};
  const brand = typeof s.brand === "string" ? (() => {
    try {
      return JSON.parse(s.brand);
    } catch (e) {
      return null;
    }
  })() : s.brand || s.brandJson || null;
  const segs = (d && Array.isArray(d.segments) ? d.segments : Array.isArray(s.segments) ? s.segments : []).map(normSegment).filter(Boolean).sort((a, b) => a.position - b.position);
  const renders = (d && Array.isArray(d.renders) ? d.renders : Array.isArray(s.renders) ? s.renders : []).map(normRender).filter(Boolean);
  return {
    sequence: {
      id: idStr(s.id),
      title: s.title || "Untitled sequence",
      purpose: String(s.purpose || "custom").toLowerCase(),
      brief: s.brief || "",
      status: String(s.status || "draft").toLowerCase(),
      formats: formats.filter(f => FORMATS.some(([k]) => k === f)),
      voice: s.voice || "Joanna",
      musicUrl: s.musicUrl || "",
      musicVolume: num(s.musicVolume, 0.35),
      settings: {
        ...DEFAULT_SETTINGS,
        ...settings
      },
      brand,
      brandKit: s.brandKit !== undefined ? !!s.brandKit : !!brand,
      scriptVersion: num(s.scriptVersion, 0),
      containerId: s.containerId ? idStr(s.containerId) : null,
      storyId: s.storyId ? idStr(s.storyId) : null,
      productId: s.productId ? idStr(s.productId) : null,
      estimatedSeconds: num(s.estimatedSeconds),
      createdDate: s.createdDate || null,
      modifiedDate: s.modifiedDate || null,
      lastRender: s.lastRender || s.lastRenders || null
    },
    segments: segs,
    renders
  };
}

/** Segment as the API wants it back (full ordered list on every save; ids kept, new ones without id). */
export function segmentToApi(s, position) {
  const out = {
    position,
    kind: s.kind,
    assetId: s.assetId || null,
    mediaUrl: s.mediaUrl || null,
    narration: s.narration || "",
    overlayText: s.overlayText || null,
    chapterTitle: s.chapterTitle || null,
    trimStart: num(s.trimStart, 0),
    trimEnd: num(s.trimEnd),
    durationSeconds: num(s.durationSeconds),
    fit: s.fit || "auto",
    motion: s.motion || "auto",
    transitionIn: s.transitionIn || "crossfade",
    audioMode: s.audioMode || "duck",
    card: s.kind === "card" ? s.card || {
      headline: "",
      sub: "",
      style: "title"
    } : null
  };
  if (s.id) out.id = s.id;
  return out;
}

// ---- sequences -------------------------------------------------------------

/** null = the route is not deployed yet (the list shows the "not available" line); [] = none. */
export const listSequences = query => softFail(call(SEQ, {
  query
})).then(d => d ? items(d).map(s => normSequence(s)).filter(Boolean).map(x => x.sequence) : null);
export const createSequence = body => call(SEQ, {
  method: "POST",
  body
}).then(normSequence);
export const getSequence = id => call(`${SEQ}/${enc(id)}`).then(normSequence);
export const updateSequence = (id, body) => call(`${SEQ}/${enc(id)}`, {
  method: "PUT",
  body
}).then(normSequence);
export const deleteSequence = id => call(`${SEQ}/${enc(id)}`, {
  method: "DELETE"
});
export const firstCut = (id, body) => call(`${SEQ}/${enc(id)}/first-cut`, {
  method: "POST",
  body: body || {}
}).then(normSequence);
export const putSegments = (id, segments) => call(`${SEQ}/${enc(id)}/segments`, {
  method: "PUT",
  body: {
    segments: segments.map(segmentToApi)
  }
}).then(d => ({
  segments: items(d && d.segments ? d.segments : d).map(normSegment).filter(Boolean),
  estimatedSeconds: num(d && d.estimatedSeconds),
  scriptVersion: num(d && d.scriptVersion)
}));
export const rewriteSegment = (id, segId, instruction) => call(`${SEQ}/${enc(id)}/segments/${enc(segId)}/rewrite`, {
  method: "POST",
  body: instruction ? {
    instruction
  } : {}
}).then(d => normSegment(d && d.segment || d));

// ---- renders ---------------------------------------------------------------

export const startRender = (id, formats) => call(`${SEQ}/${enc(id)}/render`, {
  method: "POST",
  body: formats && formats.length ? {
    formats
  } : {}
}).then(d => ({
  batchId: idStr(d && d.batchId),
  renders: items(d && d.renders ? d.renders : []).map(normRender).filter(Boolean)
}));
export const listRenders = id => softFail(call(`${SEQ}/${enc(id)}/renders`)).then(d => d ? items(d).map(normRender).filter(Boolean) : []);
export const getRender = (id, renderId) => call(`${SEQ}/${enc(id)}/renders/${enc(renderId)}`).then(normRender);

/** localStorage key per sequence so a render that outlives the page re-attaches on the next open (the plunk-video-job pattern). */
const batchKey = id => `plunk-sequence-batch:${idStr(id)}`;
export function saveBatch(id, batch) {
  try {
    localStorage.setItem(batchKey(id), JSON.stringify({
      ...batch,
      at: Date.now()
    }));
  } catch (e) {/* storage off */}
}
export function loadBatch(id) {
  try {
    const raw = localStorage.getItem(batchKey(id));
    if (!raw) return null;
    const b = JSON.parse(raw);
    if (!b || !Array.isArray(b.renderIds) || Date.now() - (b.at || 0) > 6 * 3600 * 1000) {
      clearBatch(id);
      return null;
    }
    return b;
  } catch (e) {
    return null;
  }
}
export function clearBatch(id) {
  try {
    localStorage.removeItem(batchKey(id));
  } catch (e) {/* storage off */}
}

// ---- assets (the picker and the Attach hook) ------------------------------

/** Every image and video asset (not versions), newest first; null when the assets route is missing. */
export const listAssets = query => softFail(call(`${CONTENT}/assets`, {
  query
})).then(d => d ? items(d).filter(a => a && (a.kind === "image" || a.kind === "video") && !a.parentAssetId).map(a => ({
  ...a,
  id: idStr(a.id)
})) : null);
export const linkAsset = (assetId, body) => call(`${CONTENT}/assets/${enc(assetId)}/link`, {
  method: "POST",
  body
});

/** Presigned PUT straight to S3, then register the asset (the Assets upload path). Resolves the registered asset row. */
export async function uploadAsset(file, onProgress, extra) {
  const pre = await call(`${CONTENT}/assets/upload-url`, {
    method: "POST",
    body: {
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      bytes: file.size
    }
  });
  if (!pre || !pre.uploadUrl) throw new Error(pre && pre.error || "Could not prepare the upload.");
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", pre.uploadUrl, true);
    xhr.setRequestHeader("content-type", pre.contentType || file.type || "application/octet-stream");
    xhr.upload.onprogress = e => {
      if (e.lengthComputable && onProgress) onProgress(Math.round(e.loaded / e.total * 100));
    };
    xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error("Upload failed (network)"));
    xhr.send(file);
  });
  let durationSeconds = null;
  if (pre.kind === "video") {
    try {
      durationSeconds = await new Promise(res => {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => {
          res(Math.round(v.duration || 0));
          URL.revokeObjectURL(v.src);
        };
        v.onerror = () => res(null);
        v.src = URL.createObjectURL(file);
      });
    } catch (e) {
      durationSeconds = null;
    }
  }
  const a = await call(`${CONTENT}/assets`, {
    method: "POST",
    body: {
      kind: pre.kind,
      url: pre.url,
      title: file.name.replace(/\.[a-z0-9]+$/i, ""),
      source: "upload",
      durationSeconds,
      ...(extra || {})
    }
  });
  return {
    ...(a || {}),
    id: idStr(a && (a.id || a.Id)),
    kind: pre.kind,
    url: pre.url,
    durationSeconds,
    title: file.name.replace(/\.[a-z0-9]+$/i, "")
  };
}

/**
 * Picker buckets (spec §13.1: Images / Clips / Recordings / Renders). Renders are what Plunk made (Studio scenes,
 * generated images, rendered and reframed videos); clips are cinematic shots and uploaded footage; recordings are
 * screen captures (source or title says so).
 */
export function assetBucket(a) {
  if (!a) return "image";
  const src = String(a.source || "").toLowerCase();
  const text = `${a.title || ""} ${a.tags || ""}`.toLowerCase();
  if (a.kind === "video") {
    if (src === "recording" || src === "screen_recording" || /\b(record|screen|capture|walkthrough recording)\b/.test(text)) return "recording";
    if (src === "plunk_video" || src === "plunk_sequence" || src === "plunk_reframe" || src === "edit") return "render";
    return "clip";
  }
  if (src === "shopify" || src === "catalog" || src === "feed" || src === "product" || a.productId || a.productTitle) return "product";
  if (src === "studio" || src === "plunk_image" || src === "plunk_room" || src === "edit") return "render";
  return "image";
}
export const BUCKETS = [["", "All"], ["product", "Products"], ["image", "Images"], ["clip", "Clips"], ["recording", "Recordings"], ["render", "Renders"]];

// ---- errors and formatting -------------------------------------------------

/** One place to read an ApiError: status, the spec's `code`, the message, plus the two states the views switch on. */
export function errInfo(e) {
  const status = e && e.status;
  const data = e && e.data || {};
  const code = String(data.code || "").toLowerCase();
  return {
    status,
    code,
    message: (data.error || data.message || e && e.message || "").toString(),
    data,
    soft: isSoft(e),
    insufficient: status === 402 || code === "out_of_credits" || code === "insufficient_credits" || !!(e && e.outOfCredits),
    rendering: status === 409,
    buyUrl: data.buyUrl || BUY_URL
  };
}
export const fmtClock = s => {
  const n = Math.max(0, Math.round(Number(s) || 0));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};
export const fmtSec = s => s === null || s === undefined || isNaN(Number(s)) ? "" : `${(Math.round(Number(s) * 10) / 10).toFixed(1)} s`;
export const fmtDate = d => {
  if (!d) return "";
  const t = new Date(d);
  if (isNaN(t)) return "";
  return t.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};
export const timeAgo = d => {
  if (!d) return "";
  const t = new Date(d);
  if (isNaN(t)) return "";
  const s = Math.max(0, (Date.now() - t.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return t.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  });
};
export const wordCount = t => String(t || "").trim().split(/\s+/).filter(Boolean).length;
export const purposeLabel = p => (PURPOSES.find(([k]) => k === p) || [p, p])[1];
export const formatDims = f => {
  const r = FORMATS.find(([k]) => k === f);
  return r ? `${r[3]} x ${r[4]}` : "";
};
export const aspectOf = f => f === "9:16" ? "9 / 16" : f === "1:1" ? "1 / 1" : "16 / 9";